import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Container,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import logoSmall from '../assets/10fd7012537e45f0733725550d2b62de.png';
import logoLarge from '../assets/1af3d80961feefef4a0b.png';
import { Link as RouterLink, useLocation } from 'react-router-dom';

function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isHome = location.pathname === '/';

  const handleLanguageClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setAnchorEl(null);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    handleLanguageClose();
  };

  const navItems = [
    { key: 'about', href: isHome ? '#about' : '/#about' },
    { key: 'skills', href: isHome ? '#skills' : '/#skills' },
    { key: 'experience', href: isHome ? '#experience' : '/#experience' },
    { key: 'projects', href: isHome ? '#projects' : '/#projects' },
  ];

  const languages = [
    { code: 'fr', label: 'Français' },
    { code: 'en', label: 'English' },
    { code: 'ja', label: '日本語' },
  ];

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: scrolled 
          ? 'linear-gradient(135deg, rgba(15, 20, 25, 0.98) 0%, rgba(22, 28, 36, 0.95) 100%)'
          : 'linear-gradient(135deg, rgba(15, 20, 25, 0.8) 0%, rgba(22, 28, 36, 0.7) 100%)',
        backdropFilter: 'blur(20px)',
        borderBottom: scrolled ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid transparent',
        boxShadow: scrolled ? '0 4px 20px rgba(0, 0, 0, 0.3)' : 'none',
        transition: 'all 0.3s ease',
        zIndex: 1200,
        maxWidth: '100vw',
        overflowX: 'hidden',
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Toolbar sx={{ py: { xs: 1.5, sm: 2 }, minHeight: { xs: '56px', sm: '64px' } }}>
          <Box
            component={RouterLink}
            to="/"
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              textDecoration: 'none',
            }}
          >
            <Box
              component="img"
              src={logoSmall}
              alt="Logo"
              sx={{
                height: { xs: 28, md: 36 },
                width: 'auto',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontFamily: '"Russo One", sans-serif',
                fontWeight: 400,
                fontSize: { xs: '0.9rem', md: '1.1rem' },
                color: '#ffffff',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Michaël Cadoul
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 4, alignItems: 'center' }}>
            {navItems.map((item) => (
              <Button
                key={item.key}
                component={isHome ? 'a' : RouterLink}
                to={!isHome ? item.href : undefined}
                href={isHome ? item.href : undefined}
                sx={{
                  color: '#c9d1d9',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  p: 0,
                  minWidth: 'auto',
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: '#ff6b6b',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {t(`nav.${item.key}`)}
              </Button>
            ))}
            
            <IconButton
              onClick={handleLanguageClick}
              sx={{
                color: '#c9d1d9',
                border: '2px solid rgba(255, 107, 107, 0.3)',
                borderRadius: '10px',
                padding: '8px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: '#ff6b6b',
                  color: '#ff6b6b',
                },
              }}
            >
              <LanguageIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Mobile Navigation */}
          <IconButton
            onClick={() => setMobileOpen(true)}
            sx={{ display: { xs: 'flex', md: 'none' }, color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>

          {/* Language Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleLanguageClose}
            PaperProps={{
              sx: {
                background: 'linear-gradient(135deg, rgba(22, 28, 36, 0.95) 0%, rgba(30, 40, 52, 0.95) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 107, 107, 0.2)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                borderRadius: '12px',
                mt: 1,
              },
            }}
          >
            {languages.map((lang) => (
              <MenuItem
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                selected={i18n.language === lang.code}
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  borderRadius: '8px',
                  mx: 1,
                  my: 0.5,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.15), rgba(78, 205, 196, 0.1))',
                    color: '#ff6b6b',
                  },
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.2), rgba(78, 205, 196, 0.15))',
                    color: '#ff6b6b',
                    '&:hover': {
                      background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.25), rgba(78, 205, 196, 0.2))',
                    },
                  },
                }}
              >
                {lang.label}
              </MenuItem>
            ))}
          </Menu>

          {/* Mobile Drawer */}
          <Drawer
            anchor="right"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            PaperProps={{
              sx: {
                width: 280,
                background: 'linear-gradient(135deg, rgba(15, 20, 25, 0.98) 0%, rgba(22, 28, 36, 0.98) 100%)',
                backdropFilter: 'blur(20px)',
                borderLeft: '2px solid transparent',
                borderImage: 'linear-gradient(180deg, #ff6b6b, #4ecdc4, #ffd93d) 1',
              },
            }}
          >
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton onClick={() => setMobileOpen(false)} sx={{ color: 'text.primary' }}>
                <CloseIcon />
              </IconButton>
            </Box>
            <List>
              {navItems.map((item) => (
                <ListItem key={item.key} disablePadding>
                  <ListItemButton
                    component={isHome ? 'a' : RouterLink}
                    to={!isHome ? item.href : undefined}
                    href={isHome ? item.href : undefined}
                    onClick={() => setMobileOpen(false)}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(220, 38, 38, 0.1)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={t(`nav.${item.key}`)}
                      primaryTypographyProps={{
                        sx: { fontSize: '0.875rem', color: 'text.secondary' },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>

            {/* Language Selector in Mobile Menu */}
            <Box sx={{ px: 2, pt: 2, pb: 3, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                  mb: 1.5,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                }}
              >
                {t('nav.language') || 'Language'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {languages.map((lang) => (
                  <Button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setMobileOpen(false);
                    }}
                    variant={i18n.language === lang.code ? 'contained' : 'outlined'}
                    sx={{
                      justifyContent: 'flex-start',
                      borderRadius: '10px',
                      py: 1.5,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      borderColor: i18n.language === lang.code ? '#ff6b6b' : 'rgba(255, 255, 255, 0.2)',
                      color: i18n.language === lang.code ? '#fff' : '#c9d1d9',
                      backgroundColor: i18n.language === lang.code ? '#ff6b6b' : 'transparent',
                      '&:hover': {
                        borderColor: '#ff6b6b',
                        backgroundColor: i18n.language === lang.code ? '#e85555' : 'rgba(255, 107, 107, 0.1)',
                      },
                    }}
                  >
                    {lang.label}
                  </Button>
                ))}
              </Box>
            </Box>
          </Drawer>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default Navbar;
